import { customState, TAG_STATE, TagState } from '@/components/shared/Tags.tsx';
import { OperationMode } from '@/services';
import { useApiStore } from '@core/store';
import { useMemo } from 'react';

const DEFAULT_MODE_STATE: Record<string, TagState> = {
    unmanned: TAG_STATE.info,
    closed: TAG_STATE.inactive,
    out_of_service: TAG_STATE.offline
};

const CUSTOM_MODE_STATES: TagState[] = [TAG_STATE.accent, TAG_STATE.standby];

export function buildOperationModeStateMap(operationModes: OperationMode[]): Record<string, TagState> {
    const map: Record<string, TagState> = {};

    const customModes = operationModes
        .map((m) => m.name.toLowerCase())
        .filter((m) => !DEFAULT_MODE_STATE[m])
        .sort();

    operationModes.forEach((raw) => {
        const mode = raw.name.toLowerCase();
        if (DEFAULT_MODE_STATE[mode]) {
            map[mode] = DEFAULT_MODE_STATE[mode];
        }
    });

    customModes.forEach((mode, i) => {
        map[mode] = CUSTOM_MODE_STATES[i]
            ? customState(CUSTOM_MODE_STATES[i], { label: mode })
            : customState(TAG_STATE.unknown, { label: mode, icon: undefined });
    });

    return map;
}

export function createModeTagStateGetter(modeStateMap: Record<string, TagState>) {
    return (modeName: string | null): TagState =>
        modeStateMap[modeName?.toLowerCase() ?? ''] ??
        customState(TAG_STATE.unknown, { label: modeName ?? '', icon: undefined });
}

export function useModeTagState() {
    const { operationModeStateMap } = useApiStore();
    return useMemo(() => createModeTagStateGetter(operationModeStateMap), [operationModeStateMap]);
}
